import React from 'react';
import { Typography, Box, styled } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button } from 'components/Button/Button';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import UnexpectedErrorOneSVG from 'assets/images/ErrorState/UnexpectedErrorOne.svg';
import UnexpectedErrorTwoSVG from 'assets/images/ErrorState/UnexpectedErrorTwo.svg';
import NetworkErrorSVG from 'assets/images/ErrorState/NetworkError.svg';
import {
  UnexpectedErrorOneSVG as UnexpectedErrorOneComponent,
  UnexpectedErrorTwoSVG as UnexpectedErrorTwoComponent,
  NetworkErrorSVG as NetworkErrorComponent,
} from 'components/ErrorStateSVGComp';

interface ErrorStateProps {
  imageArray: string[];
  title: string;
  subtitle: string;
  onRetryClick?: () => void;
}

/**
 * Maps SVG file imports to factory functions that create inline components for offline support
 * Using factory functions to avoid creating React elements on module load
 */
const svgToInlineComponentMap: Record<string, () => React.ReactElement> = {
  [UnexpectedErrorOneSVG]: () => <UnexpectedErrorOneComponent key="unexpected-error-one" />,
  [UnexpectedErrorTwoSVG]: () => <UnexpectedErrorTwoComponent key="unexpected-error-two" />,
  [NetworkErrorSVG]: () => <NetworkErrorComponent key="network-error" />,
};

const Container = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  fontWeight: 400,
  marginTop: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.grey[400],
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '250px',
  '& > svg': {
    width: '100%',
    height: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '180px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '235px',
  },
}));

export const ErrorState = ({ imageArray, title, subtitle, onRetryClick }: ErrorStateProps) => {
  const { theme } = useThemeUtils();
  const errorTitleId = React.useId();
  const errorSubtitleId = React.useId();
  const imageArrayWithDefault =
    imageArray && imageArray.length > 0 ? imageArray : [UnexpectedErrorOneSVG];
  const randomIndex = Math.floor(Math.random() * imageArrayWithDefault.length);
  const selectedSVGString = imageArrayWithDefault[randomIndex];
  const componentForSVG =
    svgToInlineComponentMap[selectedSVGString] || svgToInlineComponentMap[UnexpectedErrorOneSVG];
  const selectedSVG = componentForSVG?.();

  return (
    <Container aria-labelledby={errorTitleId} aria-describedby={errorSubtitleId}>
      {selectedSVG && <ImageContainer>{selectedSVG}</ImageContainer>}
      <Typography id={errorTitleId} variant="h6" color="primary">
        {title}
      </Typography>
      <Typography id={errorSubtitleId} variant="body1" color="grey.800">
        {subtitle}
      </Typography>
      {onRetryClick && (
        <StyledButton variant="tertiary" onClick={onRetryClick} aria-label="Retry">
          Retry
          <ReplayIcon
            fontSize="small"
            sx={{
              color: theme.palette.brandShade1.main,
              transform: 'scaleX(-1)',
            }}
            aria-hidden="true"
          />
        </StyledButton>
      )}
    </Container>
  );
};
